import React, { useState, useEffect } from 'react';
import { Users, ClipboardList, DollarSign, TrendingUp, ShieldCheck, BarChart3, Search, Settings, Shield } from 'lucide-react';
import { getApiBaseUrl } from '../config/api';
import { isFinanceDashboardRole, staffRole } from '../utils/staffRole';
import { fetchAnalyticsSummary } from '../services/adminAnalyticsApi';
import StatsCard from './analytics/StatsCard';
import RevenueTrendChart from './analytics/RevenueTrendChart';
import CoverDistributionChart from './analytics/CoverDistributionChart';
import TopPerformingAgentsChart from './analytics/TopPerformingAgentsChart';
import RecentTransactionsCard from './analytics/RecentTransactionsCard';
import QuickActionsCard from './analytics/QuickActionsCard';
import './DashboardOverview.css';
import '../styles/analytics-theme.css';

const DashboardOverview = ({ onTabChange, onShowNotification }) => {
  const [realData, setRealData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compSummary, setCompSummary] = useState(null);
  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('adminToken');
        
        if (!token) {
          if (onShowNotification) {
            onShowNotification('No authentication token found', 'warning');
          }
          setRealData({
            totalAgents: 0,
            totalCovers: 0,
            totalRevenue: 0,
            monthlyRevenue: 0,
            pendingKYC: 0,
            recentPolicies: []
          });
          setLoading(false);
          return;
        }

        let storedUser = {};
        try {
          storedUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
        } catch (_) { /* ignore */ }
        if (!isFinanceDashboardRole(storedUser)) {
          setRealData({
            totalAgents: 0,
            totalCovers: 0,
            totalRevenue: 0,
            monthlyRevenue: 0,
            pendingKYC: 0,
            recentPolicies: []
          });
          setLoading(false);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setRealData(result.data);
          } else {
            if (onShowNotification) {
              onShowNotification('Dashboard API returned unsuccessful response', 'warning');
            }
            setRealData({
              totalAgents: 0,
              totalCovers: 0,
              totalRevenue: 0,
              monthlyRevenue: 0,
              pendingKYC: 0,
              recentPolicies: []
            });
          }
        } else {
          let errorMessage = `Failed to fetch dashboard data (${response.status})`;
          try {
            const errorData = await response.json();
            errorMessage = errorData.error || errorMessage;
            console.error('Dashboard API error:', errorData);
          } catch (e) {
            console.error('Dashboard API error:', response.status, response.statusText);
          }
          
          if (onShowNotification) {
            onShowNotification(errorMessage, 'warning');
          }
          setRealData({
            totalAgents: 0,
            totalCovers: 0,
            totalRevenue: 0,
            monthlyRevenue: 0,
            pendingKYC: 0,
            recentPolicies: []
          });
        }
      } catch (error) {
        console.error('Dashboard fetch error:', error);
        if (onShowNotification) {
          onShowNotification('Error loading dashboard data. Please refresh the page.', 'error');
        }
        setRealData({
          totalAgents: 0,
          totalCovers: 0,
          totalRevenue: 0,
          monthlyRevenue: 0,
          pendingKYC: 0,
          recentPolicies: []
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onShowNotification]);

  useEffect(() => {
    let cancelled = false;
    const loadComp = async () => {
      try {
        let storedUser = {};
        try {
          storedUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
        } catch (_) { /* ignore */ }
        if (staffRole(storedUser) !== 'super_admin') {
          if (!cancelled) setCompSummary(null);
          return;
        }
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/admin/comprehensive/issued/summary`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });
        const data = await res.json().catch(() => ({}));
        if (!cancelled && res.ok && data.success) setCompSummary(data.data || null);
      } catch (_) {
        if (!cancelled) setCompSummary(null);
      }
    };
    loadComp();
    return () => { cancelled = true; };
  }, [API_BASE_URL]);

  const [chartData, setChartData] = useState(null);
  const [chartsLoading, setChartsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadCharts = async () => {
      try {
        let storedUser = {};
        try {
          storedUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
        } catch (_) { /* ignore */ }
        if (!isFinanceDashboardRole(storedUser)) {
          if (!cancelled) {
            setChartData(null);
            setChartsLoading(false);
          }
          return;
        }
        setChartsLoading(true);
        const summary = await fetchAnalyticsSummary();
        if (!cancelled) setChartData(summary);
      } catch (error) {
        console.warn('Analytics charts fetch:', error.message);
        if (!cancelled) setChartData(null);
      } finally {
        if (!cancelled) setChartsLoading(false);
      }
    };
    loadCharts();
    return () => { cancelled = true; };
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-KE').format(num);
  };

  const displayData = realData;
  const recentPolicies = displayData?.recentPolicies || [];

  const statsCards = [
    {
      title: 'Total Agents',
      value: formatNumber(displayData?.totalAgents || 0),
      subtitle: 'Active insurance agents',
      icon: Users,
      variant: 'default',
    },
    {
      title: 'Total Transactions',
      value: formatNumber(displayData?.totalCovers || 0),
      subtitle: 'Completed payments (production)',
      icon: ClipboardList,
      variant: 'default',
      onClick: onTabChange ? () => onTabChange('revenue') : undefined,
    },
    {
      title: 'Total Revenue',
      value: formatCurrency(displayData?.totalRevenue || 0),
      subtitle: 'Lifetime production (paid premium)',
      icon: DollarSign,
      variant: 'primary',
      onClick: onTabChange ? () => onTabChange('revenue') : undefined,
    },
    {
      title: 'Monthly Revenue',
      value: formatCurrency(displayData?.monthlyRevenue || 0),
      subtitle: "This month's production",
      icon: TrendingUp,
      variant: 'default',
      onClick: onTabChange ? () => onTabChange('revenue') : undefined,
    },
    {
      title: 'Pending KYC',
      value: formatNumber(displayData?.pendingKYC || 0),
      subtitle: 'Awaiting verification',
      icon: ShieldCheck,
      variant: 'default',
      onClick: onTabChange ? () => onTabChange('kyc') : undefined,
    },
    ...(compSummary
      ? [
          {
            title: 'COMP Issued',
            value: formatNumber(compSummary.totalIssued || 0),
            subtitle: `${formatNumber(compSummary.issuedThisMonth || 0)} this month · ${formatCurrency(compSummary.totalPremium || 0)}`,
            icon: Shield,
            variant: 'primary',
            onClick: onTabChange ? () => onTabChange('comprehensive-issued') : undefined,
          },
        ]
      : []),
  ];

  const quickActions = [
    {
      id: 'reports',
      title: 'Generate Report',
      description: 'Export policy and revenue data',
      icon: BarChart3,
      accent: 'primary',
      onClick: () => onTabChange && onTabChange('reports'),
    },
    {
      id: 'search',
      title: 'Search Transactions',
      description: 'Find specific policies quickly',
      icon: Search,
      accent: 'sky',
      onClick: () => onTabChange && onTabChange('transactions'),
    },
    ...(compSummary
      ? [
          {
            id: 'comprehensive-issued',
            title: 'Comprehensive Issued',
            description: 'Track COMP certificates & Core status',
            icon: Shield,
            accent: 'amber',
            onClick: () => onTabChange && onTabChange('comprehensive-issued'),
          },
        ]
      : []),
    {
      id: 'settings',
      title: 'Settings',
      description: 'Configure system preferences',
      icon: Settings,
      accent: 'emerald',
      onClick: () => onTabChange && onTabChange('settings'),
    },
  ];

  if (loading) {
    return (
      <div className="dashboard-overview ab-page ab-surface-light">
        <div className="loading-skeleton">
          <div className="skeleton-cards">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-card"></div>
            ))}
          </div>
          <div className="skeleton-chart"></div>
          <div className="skeleton-table"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-overview ab-page ab-surface-light">
      <div className="ab-stats-grid ab-stats-grid-5">
        {statsCards.map((card, index) => (
          <StatsCard
            key={index}
            title={card.title}
            value={card.value}
            subtitle={card.subtitle}
            icon={card.icon}
            variant={card.variant}
            onClick={card.onClick}
          />
        ))}
      </div>

      {!chartsLoading && chartData && (
        <>
          <div className="ab-charts-row two-thirds">
            <RevenueTrendChart trendData={chartData.trends?.monthly || []} />
            <CoverDistributionChart distribution={chartData.distribution || []} />
          </div>
          <div style={{ marginBottom: '1.5rem' }}>
            <TopPerformingAgentsChart topAgents={chartData.topAgents || []} />
          </div>
        </>
      )}

      <div className="ab-dashboard-bottom">
        <RecentTransactionsCard
          items={recentPolicies}
          onViewAll={() => onTabChange && onTabChange('transactions')}
        />
        <QuickActionsCard actions={quickActions} />
      </div>
    </div>
  );
};

export default DashboardOverview;
